import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import ErrorCatch from './pages/ErrorCatch';
import CajaQA from './pages/CajaQA';
import { useAuthStore } from './store/auth';
import { loadAllExtras } from './services/clientesExtra';
import Layout from './components/Layout';
import VanessaWidget from './components/VanessaWidget';
// Login eager (primera vista cuando no hay sesión)
import Login from './pages/Login';

// Páginas lazy: cada una se descarga al navegar a su ruta
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Agents = lazy(() => import('./pages/Agents'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Reporting = lazy(() => import('./pages/Reporting'));
const FlowDiagram = lazy(() => import('./pages/FlowDiagram'));
const DTFOrders = lazy(() => import('./pages/DTFOrders'));
const DTFInventario = lazy(() => import('./pages/DTFInventario'));
const MontajeTPU = lazy(() => import('./pages/MontajeTPU'));
const AIStudio = lazy(() => import('./pages/AIStudio'));
const ColaTPU = lazy(() => import('./pages/ColaTPU'));
const DTFImportaciones = lazy(() => import('./pages/DTFImportaciones'));
const DTFComprasCO = lazy(() => import('./pages/DTFComprasCO'));
const MundialEstampados = lazy(() => import('./pages/MundialEstampados'));
const CatalogoMundial = lazy(() => import('./pages/CatalogoMundial'));
const MundialPaises = lazy(() => import('./pages/mundial/Paises'));
const MundialEquiposPais = lazy(() => import('./pages/mundial/EquiposPais'));
const MundialCamisetasEquipo = lazy(() => import('./pages/mundial/CamisetasEquipo'));
const PedidoMundial = lazy(() => import('./pages/PedidoMundial'));
const Posters = lazy(() => import('./pages/Posters'));
const CentroCostos = lazy(() => import('./pages/CentroCostos'));
const DTFDashboard = lazy(() => import('./pages/DTFDashboard'));
const DTFCuentas = lazy(() => import('./pages/DTFCuentas'));
const DTFClientes = lazy(() => import('./pages/DTFClientes'));
const DTFEtiquetas = lazy(() => import('./pages/DTFEtiquetas'));
const DTFWhatsApp = lazy(() => import('./pages/DTFWhatsApp'));
const AuraScreen = lazy(() => import('./pages/AuraScreen'));
const Augustito = lazy(() => import('./pages/Augustito'));
const Logistica = lazy(() => import('./pages/Logistica'));
const DTFWhatsAppLog = lazy(() => import('./pages/DTFWhatsAppLog'));
const DTFReorden = lazy(() => import('./pages/DTFReorden'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const ChatAgent = lazy(() => import('./pages/ChatAgent'));
const Facel = lazy(() => import('./pages/Facel'));
const SubliV3Hub = lazy(() => import('./pages/subli-v3/Hub'));
const SubliV3Molds = lazy(() => import('./pages/subli-v3/Molds'));
const SubliV3MoldEditor = lazy(() => import('./pages/subli-v3/MoldEditor'));
const SubliV3Designs = lazy(() => import('./pages/subli-v3/Designs'));
const SubliV3DesignNew = lazy(() => import('./pages/subli-v3/DesignNew'));
const SubliV3DesignDetail = lazy(() => import('./pages/subli-v3/DesignDetail'));
const SubliV3Palettes = lazy(() => import('./pages/subli-v3/Palettes'));
const SubliV3Texturas = lazy(() => import('./pages/subli-v3/Texturas'));
const SubliV3Preview = lazy(() => import('./pages/subli-v3/Preview'));
const WizardLogosLogin = lazy(() => import('./pages/WizardLogos/Login'));
const WizardLogosPaint = lazy(() => import('./pages/WizardLogos/Paint'));
const WizardLogosAdmin = lazy(() => import('./pages/WizardLogos/Admin'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="w-10 h-10 border-3 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <Navigate to="/dtf/ordenes" replace /> : <>{children}</>;
}

export default function App() {
  // Cargar datos de clientes del servidor y sincronizar con localStorage
  useEffect(() => {
    // Cargar extras en memoria (el servicio mantiene su propio cache interno)
    loadAllExtras().catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/wizard-logos" element={<WizardLogosLogin />} />
        <Route path="/wizard-logos/paint" element={<WizardLogosPaint />} />
        <Route path="/wizard-logos/single" element={<WizardLogosPaint singleMode />} />
        <Route path="/mundial" element={<ErrorCatch><MundialPaises /></ErrorCatch>} />
        <Route path="/posters" element={<ErrorCatch><Posters /></ErrorCatch>} />
        <Route path="/mundial/pedido/:id" element={<PedidoMundial />} />
        <Route path="/mundial/pais/:paisSlug" element={<ErrorCatch><MundialEquiposPais /></ErrorCatch>} />
        <Route path="/mundial/equipo/:tipoEquipo/:equipoSlug" element={<ErrorCatch><MundialCamisetasEquipo /></ErrorCatch>} />
        <Route path="/mundial/:slug" element={<ErrorCatch><MundialPaises /></ErrorCatch>} />
        <Route
          path="/login"
          element={<PublicRoute><Login /></PublicRoute>}
        />
        <Route
          path="/"
          element={<ProtectedRoute><Layout /></ProtectedRoute>}
        >
          <Route index element={<Navigate to="/dtf/ordenes" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="agents" element={<Agents />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="reporting" element={<Reporting />} />
          <Route path="diagrama" element={<FlowDiagram />} />
          <Route path="dtf/dashboard" element={<DTFDashboard />} />
          <Route path="caja-qa" element={<CajaQA />} />
          <Route path="centro-costos" element={<CentroCostos />} />
          <Route path="dtf/ordenes" element={<DTFOrders />} />
          <Route path="dtf/cuentas" element={<DTFCuentas />} />
          <Route path="dtf/clientes" element={<DTFClientes />} />
          <Route path="dtf/etiquetas" element={<DTFEtiquetas />} />
          <Route path="dtf/whatsapp" element={<DTFWhatsApp />} />
          <Route path="dtf/wa-log" element={<DTFWhatsAppLog />} />
          <Route path="dtf/aura-screen" element={<AuraScreen />} />
          <Route path="augustito" element={<Augustito />} />
          <Route path="logistica" element={<Logistica />} />
          <Route path="dtf/inventario" element={<DTFInventario />} />          <Route path="dtf/cola-tpu" element={<ColaTPU />} />
          <Route path="dtf/importaciones" element={<DTFImportaciones />} />
          <Route path="compras-co" element={<DTFComprasCO />} />
          <Route path="dtf/mundial" element={<MundialEstampados />} />
          <Route path="dtf/montaje" element={<MontajeTPU />} />
          <Route path="dtf/ai-studio" element={<AIStudio />} />
          <Route path="dtf/reorden" element={<DTFReorden />} />
          <Route path="admin/usuarios" element={<AdminUsers />} />
          <Route path="dtf/wizard-users" element={<WizardLogosAdmin />} />
          <Route path="chat" element={<ChatAgent />} />
          <Route path="subli-v3" element={<SubliV3Hub />} />
          <Route path="subli-v3/palettes" element={<SubliV3Palettes />} />
          <Route path="subli-v3/texturas" element={<SubliV3Texturas />} />
          <Route path="subli-v3/molds" element={<SubliV3Molds />} />
          <Route path="subli-v3/preview/:id" element={<SubliV3Preview />} />
          <Route path="subli-v3/molds/:id" element={<SubliV3MoldEditor />} />
          <Route path="subli-v3/designs" element={<SubliV3Designs />} />
          <Route path="subli-v3/designs/new" element={<SubliV3DesignNew />} />
          <Route path="subli-v3/designs/:id" element={<SubliV3DesignDetail />} />
          <Route path="admin/facel" element={<Facel />} />
        </Route>
        <Route path="*" element={<Navigate to="/dtf/ordenes" replace />} />
      </Routes>
      </Suspense>
      <VanessaWidget />
    </BrowserRouter>
  );
}
